import { useState, MouseEvent, useEffect, useMemo, useCallback } from "react";
import { ReadAcrossDAGRequestModel } from "../../../api/models"
import { ReactComponent as PlusIcon } from '../../../assets/Icons/adornments/Plus.svg';
import { ReactComponent as BeakerIcon } from '../../../assets/Icons/navigation/Read-Across.svg';
import Buttons, { Btn } from "../../../components/Buttons/Buttons";
import PopupHeader from "../../../components/Popups/PopupHeader";
import PopupSideNav from "../../../components/Popups/PopupSideNav";
import PopupFooter from "../../../components/Popups/PopupFooter";
import SetRAParameters from "./GenerateRAComponents/SetRAParameters";
import SelectCompound from "../../Resultsets/components/RunPredictionComponents/SelectCompound";
import SuccessMessageModal from "../../../components/Popups/SuccessMessageModal";
import usePost from "../../../hooks/endpoints/usePost";
import ModalBase from "../../../components/Popups/ModalBase";
import SelectInternalDataset from "./GenerateRAComponents/SelectInternalDataset";

const defaultForm: ReadAcrossDAGRequestModel = {
    name: '',
    readacross_type: 'toxref',
    smiles: '',
    model_type: 'classification',
    weighting: 'tanimoto',
    num_neighbors: 10,
    dataset: undefined,
    fp_type: 'ecfp6_1024',
    tags: [],
}


export default function GenerateReadAcrossModal({
    setPopupMessage,
    selectedCompound,
    location,
}: {
    setPopupMessage: (popupMessage: string, isSuccessMessage: boolean) => void,
    selectedCompound?: string,
    location: 'modal' | 'dropdown' | 'header' | 'icon',
}) {
    const [open, setOpen] = useState(false);
    const handleOpen = () => setOpen(true);
    const [ showSelect, setShowSelect ] = useState(false);

    const [ selectedNavIndex, setSelectedNavIndex ] = useState(0);
    const [ btns, setBtns ] = useState<Btn[]>([]);
    const [ isDisabled, setIsDisabled ] = useState(false);
    const [ isLoading, setIsLoading ] = useState(false);
    const [ isSuccess, setIsSuccess ] = useState(false);

    const [ smiles, setSmiles ] = useState('');
    const [ numNeighbors, setNumNeighbors ] = useState(10);
    const [ selectedDataset, setSelectedDataset ] = useState<{ id: string, name: string } | null>(null);
    const [ selectedDSType, setSelectedDSType ] = useState<{ value: string, label: string }>({ value: 'datasets', label: 'Datasets' });
    const [ form, setForm ] = useState<ReadAcrossDAGRequestModel>(defaultForm);

    const { handleReadacrossPrediction } = usePost({
        setPopupMessage,
        setIsDisabled,
        setIsLoading,
        setIsSuccess,
    });

    const navItems = useMemo(() => {
        if (!form.readacross_type) return [];

        if (form.readacross_type === 'toxref') return ['Select Compound', 'Set Parameters'];

        return ['Select Compound', 'Set Parameters', 'Select Internal Dataset'];
    }, [form.readacross_type]);

    useEffect(() => {
        if (selectedCompound) {
            setSmiles(selectedCompound);
            setSelectedNavIndex(1);
        }
    }, [selectedCompound]);


    
    const handleClose = () => {
        setOpen(false);
        resetValues();
    }

    const resetValues = () => {
        setSelectedNavIndex(0);
        setShowSelect(false);
        setIsDisabled(false);
        setIsLoading(false);
        setIsSuccess(false);
        setSmiles('');
        setNumNeighbors(10);
        setForm(defaultForm);
        setSelectedDataset(null);
        setSelectedDSType({ value: 'datasets', label: 'Datasets' })
    }

    useEffect(() => {
        setBtns([
            {
                type: "tertiary",
                styling: "outline",
                id: 'cancel-read-across',
                label: 'Cancel',
                action: (e: MouseEvent<HTMLButtonElement>) => {handleClose()},
            },
            {
                type: isDisabled ? 'disabled' : 'primary',
                styling: "solid",
                id: 'run-read-across',
                label: "Run",
                action: async (e: MouseEvent<HTMLButtonElement>) => { handleReadacrossPrediction(e, form, smiles, numNeighbors) },
                isLoading,
            }
        ]);

        //eslint-disable-next-line
    }, [isDisabled, isLoading, form, smiles, numNeighbors]);

    useEffect(() => {
        if (!smiles || !form.name || (form.readacross_type! === 'internal' && !form.dataset)) {
            setIsDisabled(true);
        } else {
            setIsDisabled(false);
        }
    }, [smiles, form]);

    const checkIfLocked = (index: number) => {
        if (navItems[index] === 'Set Parameters') {
            if (!smiles) {
                return true;
            } else {
                return false;
            }
        } else {
            return false;
        }
    }

    const checkIfComplete = (index: number) => {
        if (smiles) {
            if (navItems[index] === 'Select Compound') {
                return true;
            } else if (navItems[index] === 'Set Parameters') {
                if (!form.name) {
                    return false;
                } else {
                    return true;
                }
            } else {
                if (form.readacross_type && form.readacross_type === 'internal' && !form.dataset) {
                    return false;
                } else {
                    return true;
                }
            }
        } else {
            return false;
        }

    };

    const updateDatasetId = useCallback((id: string, name: string) => {
        setSelectedDataset({ id, name });
        setForm((prevForm) => ({
            ...prevForm,
            dataset: {
                dataset_hash: id,
                structure_column: '',
                ignored_columns: [],
                name_column: '',
            }
        }));
    }, []);

    const updateSelectedDSType = useCallback((option: { value: string, label: string }) => {
        setSelectedDSType(option);

        if (option.value !== selectedDSType.value) {
            setForm((prevForm) => ({
                ...prevForm,
                dataset: undefined,
            }));
            setSelectedDataset(null);
        }
    }, [selectedDSType.value]);

    const updateRAType = useCallback((option: { value: string, label: string }) => {
        if (form.readacross_type !== option.value) {
            if (option.value === 'toxref') {
                setSelectedDataset(null);
            }
        }

        setForm((prevForm) => ({
            ...prevForm,
            readacross_type: option.value as 'internal' | 'toxref',
            dataset: prevForm.readacross_type !== option.value ? option.value === 'toxref' ? undefined : prevForm.dataset : prevForm.dataset,
        }));
    }, [form.readacross_type]);

    // console.log(form, 'form')
    

    return (
        <div>
            {location === 'dropdown' ? (
                <button onClick={handleOpen} className={`w-full px-5 py-3 text-left font-medium flex justify-between items-center gap-3 w-full h-full`}>
                    <div className='flex justify-start items-center gap-3'>
                        <p className='text-nowrap'>Generate Read-Across</p>
                    </div>
                </button>
            ) : location === 'icon' ? (
                <div className="h-full flex justify-center items-center">
                    <button onClick={handleOpen} className="w-[22px] text-disable hover:text-primary transition-all">
                        <BeakerIcon/>
                    </button>
                </div>
            ) : (
                <Buttons 
                    id='generate-read-across-btn' 
                    action={handleOpen} 
                    type="primary" 
                    label="Generate Read-Across" 
                    styling={location === 'modal' ? 'outline' : 'solid'}
                    startingAdornment={location === 'modal' ? < BeakerIcon/> : <PlusIcon/>}
                />
            )}
            <ModalBase
                open={open}
                setShowSelect={setShowSelect}
                modalId="generate-read-across-popup"
            >
            {!isSuccess ? (
                <div className={`bg-white rounded-lg w-full max-w-[900px] popup-max-height mx-auto`}>
                    <div className='flex flex-col justify-center items-stretch popup-max-height'>
                        <PopupHeader
                            label="Generate Read-Across"
                            hasOutline={true}
                            closeFn={handleClose}
                        />   
                        <div className="w-full flex justify-center items-stretch overflow-y-auto">
                            <PopupSideNav 
                                items={navItems.map((item, i) => ({
                                    label: item,
                                    type: 'number' as any,
                                    isDisabled: checkIfLocked(i),
                                    isActive: selectedNavIndex === i ? true : false,
                                    isComplete: checkIfComplete(i),
                                    action: (e: MouseEvent<HTMLButtonElement>) => {setSelectedNavIndex(i)},
                                }))}
                                selectedIndex={selectedNavIndex}
                            />
                            <div className="w-full grow overflow-y-auto border-l border-outline">
                                <div className={`w-full p-6 flex flex-col gap-8 justify-start items-stretch min-h-[600px]`}>
                                    {navItems[selectedNavIndex] === 'Select Compound' ? (
                                        <SelectCompound 
                                            smiles={smiles}
                                            setSmiles={setSmiles}
                                        />
                                    ) : navItems[selectedNavIndex] === 'Set Parameters' ?  (
                                        <SetRAParameters 
                                            form={form}
                                            setForm={setForm}
                                            showSelect={showSelect}
                                            numNeighors={numNeighbors}
                                            setNumNeighbors={setNumNeighbors}
                                            selectedDsType={selectedDSType}
                                            updateSelectedDSType={updateSelectedDSType}
                                            updateRAType={updateRAType}
                                        />
                                    ) : (
                                        <SelectInternalDataset 
                                            selectedDataset={selectedDataset}
                                            updateDatasetId={updateDatasetId}
                                            selectedDSType={selectedDSType.value}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                        <PopupFooter 
                            hasOutline={true}
                            buttons={btns}
                        />
                    </div>
                </div>
            ) : (
                <SuccessMessageModal 
                    handleClose={handleClose}
                    message="Your read-across prediction is being generated. You can check its progress in the Jobs Queue. Once the job is complete, the results will appears in the Read-Across page."
                />
            )}
            </ModalBase>
        </div>
    )
}